import { type ReactNode } from 'react';
import { Alert, Box, LinearProgress, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

type Props = {
  allowance: number;
  used: number;
  remaining: number | null;
  unlimited: boolean;
  paymentMethodOnFile: boolean;
  meteredRatePerRo: number;
  addPaymentMethodButton?: ReactNode;
};

const METERED_RATE_USD_DEFAULT = 3;

export const UsageProgress = ({
  allowance,
  used,
  remaining,
  unlimited,
  paymentMethodOnFile,
  meteredRatePerRo = METERED_RATE_USD_DEFAULT,
  addPaymentMethodButton,
}: Props) => {
  const { t } = useTranslation();

  if (unlimited) {
    return <Typography variant="body1">{t('subscription.usage.unlimited')}</Typography>;
  }

  const progressValue = allowance > 0 ? Math.min(100, (used / allowance) * 100) : 0;
  const isExhausted = (remaining ?? 0) <= 0;
  const showExhaustedNoCcAlert = isExhausted && !paymentMethodOnFile;
  const showExhaustedWithCcMessage = isExhausted && paymentMethodOnFile;
  const showSecondaryCta = !isExhausted && !paymentMethodOnFile;

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="body1" sx={{ mb: 1 }}>
          {t('subscription.usage.usedOfAllowance', { used, allowance })}
        </Typography>
        <LinearProgress variant="determinate" value={progressValue} />
      </Box>

      {showExhaustedNoCcAlert && (
        <Alert severity="error" action={addPaymentMethodButton ?? null} sx={{ alignItems: 'center' }}>
          {t('subscription.usage.exhaustedNoCc')}
        </Alert>
      )}

      {showExhaustedWithCcMessage && (
        <Typography variant="body2" color="text.secondary">
          {t('subscription.usage.exhaustedWithCc', { rate: meteredRatePerRo })}
        </Typography>
      )}

      {showSecondaryCta && addPaymentMethodButton && <Box>{addPaymentMethodButton}</Box>}
    </Stack>
  );
};
